import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import Button from '../button';
import { formatToNaira } from 'functions/currencyConveter';
import InputField from 'components/shared/input-field';
import Input from 'components/shared/input-field/inputAmountTags';
import { IoAlertCircle } from 'react-icons/io5';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { appAxios } from 'api/axios';
import { sendCatchFeedback, sendFeedback } from 'functions/feedback';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toast } from 'react-toastify';

// Define formatDate as a top-level function for consistent date formatting
const formatDate = (date: Date | null) => {
  if (!date) return '';
  // Return date in dd/mm/yyyy format
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Add the standardizeDateFormat function for consistent date handling
const standardizeDateFormat = (dateStr: string | undefined): string => {
  if (!dateStr) return '';

  try {
    // Helper function to format date to DD/MM/YYYY
    const formatToDateString = (date: Date): string => {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };

    // Handle ISO format
    if (dateStr.includes('T')) {
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        return formatToDateString(date);
      }
    }

    // Handle YYYY-MM-DD format
    if (dateStr.includes('-')) {
      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      if (!isNaN(date.getTime())) {
        return formatToDateString(date);
      }
    }

    // If already in DD/MM/YYYY format, validate it
    if (dateStr.includes('/')) {
      const [day, month, year] = dateStr.split('/').map(Number);
      const date = new Date(year, month - 1, day);

      // Check if it's a valid date
      if (
        !isNaN(date.getTime()) &&
        date.getDate() === day &&
        date.getMonth() === month - 1 &&
        date.getFullYear() === year
      ) {
        return dateStr; // Already in correct format
      }
    }

    // If we can't parse it in any standard way, try general parsing
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      return formatToDateString(date);
    }

    console.error('Could not standardize date format:', dateStr);
    return dateStr; // Return original if all parsing fails
  } catch (e) {
    console.error('Error standardizing date:', e, dateStr);
    return dateStr;
  }
};

// Helper function to parse any date format
const parseAnyDateFormat = (dateString: string | undefined): Date | null => {
  if (!dateString) return null;

  try {
    // Handle DD/MM/YYYY format
    if (dateString.includes('/')) {
      const [day, month, year] = dateString.split('/').map(Number);
      // Use UTC to avoid timezone issues
      const date = new Date(Date.UTC(year, month - 1, day));

      if (!isNaN(date.getTime())) {
        return date;
      }
    }

    // Handle YYYY-MM-DD format
    if (dateString.includes('-')) {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }

    // Try generic Date parsing
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date;
    }

    return null;
  } catch (error) {
    console.error('Error parsing date:', error);
    return null;
  }
};

// Move the calculateTotalContributions function outside of component so it can be used elsewhere
const calculateTotalContributions = (values: any) => {
  if (
    !values.preferredAmount ||
    !values.startDate ||
    !values.endDate ||
    !values.savingsFrequency
  ) {
    return 0;
  }

  const contributionAmount = parseFloat(
    typeof values.preferredAmount === 'string' 
      ? values.preferredAmount.replace?.(/,/g, '') || values.preferredAmount
      : values.preferredAmount.toString()
  );

  // Parse start and end dates
  const startDate = parseAnyDateFormat(values.startDate);
  const endDate = parseAnyDateFormat(values.endDate);

  if (!startDate || !endDate) {
    return 0;
  }

  // Calculate time difference
  const timeDiff = endDate.getTime() - startDate.getTime();
  const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
  const weeksDiff = Math.ceil(daysDiff / 7);
  const monthsDiff =
    (endDate.getFullYear() - startDate.getFullYear()) * 12 +
    (endDate.getMonth() - startDate.getMonth());

  // Calculate total contributions based on frequency
  let totalContributions = 0;
  switch (values.savingsFrequency.toLowerCase()) {
    case 'daily':
      totalContributions = contributionAmount * daysDiff;
      break;
    case 'weekly':
      totalContributions = contributionAmount * weeksDiff;
      break;
    case 'monthly':
      totalContributions = contributionAmount * monthsDiff;
      break;
  }

  return totalContributions;
};

// Advanced suggested amount calculation function
const calculateSuggestedAmount = (
  targetAmount: number,
  frequency: string,
  duration: string | undefined,
  startDateStr: string | undefined,
  endDateStr: string | undefined
) => {
  if (!targetAmount || !frequency) {
    return null;
  }

  try {
    if (isNaN(targetAmount) || targetAmount <= 0) {
      return null;
    }

    // Parse dates if available
    const startDate = parseAnyDateFormat(startDateStr);
    const endDate = parseAnyDateFormat(endDateStr);

    // If we have valid start and end dates, calculate based on that
    if (startDate && endDate) {
      // Calculate time difference
      const timeDiff = endDate.getTime() - startDate.getTime();
      const daysDiff = Math.max(1, Math.ceil(timeDiff / (1000 * 3600 * 24)));

      let periods = 1;
      switch (frequency.toLowerCase()) {
        case 'daily':
          periods = daysDiff;
          break;
        case 'weekly':
          periods = Math.max(1, Math.ceil(daysDiff / 7));
          break;
        case 'monthly':
          periods = Math.max(1, 
            (endDate.getFullYear() - startDate.getFullYear()) * 12 +
            (endDate.getMonth() - startDate.getMonth())
          );
          break;
      }
      
      return Math.ceil(targetAmount / periods);
    }

    // If we don't have dates but have duration, estimate based on duration
    if (duration) {
      let months = 1;
      if (duration === '1 Month') months = 1;
      else if (duration === '3 Months') months = 3; 
      else if (duration === '6 Months') months = 6;
      else if (duration === '9 Months') months = 9;
      else if (duration === '1 Year') months = 12;
      
      let periods = months;
      if (frequency.toLowerCase() === 'daily') periods = months * 30;
      else if (frequency.toLowerCase() === 'weekly') periods = months * 4;
      
      return Math.ceil(targetAmount / periods);
    }

    // Fallback to default calculation
    const defaultPeriods = {
      daily: 30,
      weekly: 4,
      monthly: 1
    };
    
    return Math.ceil(targetAmount / defaultPeriods[frequency.toLowerCase() as keyof typeof defaultPeriods]);
  } catch (error) {
    console.error('Error calculating suggested amount:', error);
    return null;
  }
};

interface DetailItem {
  label: string;
  value: string;
  isPercentage?: boolean;
  isInterest?: boolean;
  isSuccess?: boolean;
}

interface ReinvestModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  amount: string;
  details: DetailItem[];
  onReinvest?: (values: any) => void;
  goalId?: string;
}

const validationSchema = yup.object({
  newSavingsGoal: yup.string().required('Savings goal is required'),
  savingsDuration: yup.string().required('Duration is required'),
  savingsFrequency: yup.string().required('Savings frequency is required'),
  preferredAmount: yup
    .number()
    .required('Preferred amount is required')
    .min(1, 'Amount must be greater than 0'),
  startDate: yup
    .string()
    .required('Start date is required')
    .test('valid-date', 'Invalid date format', function (value) {
      if (!value) return false;

      // Always parse as string first
      if (typeof value !== 'string') {
        value = String(value);
      }

      // Check for DD/MM/YYYY format
      if (value.includes('/')) {
        const dateRegex = /^\d{1,2}\/\d{1,2}\/\d{4}$/;
        if (!dateRegex.test(value)) {
          return false;
        }

        try {
          // Parse and validate the date
          const [day, month, year] = value.split('/').map(Number);
          const date = new Date(year, month - 1, day);

          const isValid =
            date.getDate() === day &&
            date.getMonth() === month - 1 &&
            date.getFullYear() === year;

          return isValid;
        } catch (e) {
          console.error('Error parsing DD/MM/YYYY date:', e);
          return false;
        }
      }

      // Try to parse as ISO format (YYYY-MM-DD)
      if (value.includes('-')) {
        try {
          const date = new Date(value);
          const isValid = !isNaN(date.getTime());
          return isValid;
        } catch (e) {
          console.error('Error parsing ISO date:', e);
          return false;
        }
      }

      // Try general date parsing as fallback
      try {
        const date = new Date(value);
        return !isNaN(date.getTime());
      } catch (e) {
        console.error('Error with general date parsing:', e);
        return false;
      }
    }),
  endDate: yup
    .string()
    .required('End date is required')
    .test('valid-date', 'Invalid date format', function (value) {
      if (!value) return false;

      // Use same validation logic as startDate
      return this.parent.startDate ? true : false; // If startDate passes, this should too with same logic
    })
    .test('is-after-start-date', 'End date must be after start date', function (value) {
      const { startDate } = this.parent;
      if (!startDate || !value) return false;

      const start = parseAnyDateFormat(startDate);
      const end = parseAnyDateFormat(value);

      if (!start || !end) return false;
      return end > start;
    }),
});

const ReinvestModal: React.FC<ReinvestModalProps> = ({
  isOpen,
  onClose,
  title,
  amount,
  details,
  onReinvest,
  goalId,
}) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [amountExceedsGoal, setAmountExceedsGoal] = useState(false);
  const [suggestedAmount, setSuggestedAmount] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState<string | null>(null);
  const [customDate, setCustomDate] = useState<Date | null>(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const durationOptions = [
    '1 Month',
    '3 Months',
    '6 Months',
    '9 Months',
    '1 Year',
    'Customize',
  ];
  const frequencyOptions = ['Daily', 'Weekly', 'Monthly'];

  // Set today's date in DD/MM/YYYY format
  const today = formatDate(new Date());

  const formik = useFormik({
    initialValues: {
      newSavingsGoal: '',
      savingsDuration: '',
      savingsFrequency: '',
      preferredAmount: 0,
      startDate: today,
      endDate: '',
      autoWithdrawal: false,
    },
    validationSchema,
    onSubmit: async (values) => {
      if (amountExceedsGoal) return;
      
      // Calculate if contributions will meet the goal
      const totalContributions = calculateTotalContributions(values);
      const targetAmount = parseFloat(values.newSavingsGoal.replace(/,/g, '') || '0');
      const willMeetGoal = Math.abs(totalContributions - targetAmount) < 0.01; // Account for floating point precision
      const isShortfall = totalContributions < targetAmount;
      const isExcess = totalContributions > targetAmount;
      
      // If contributions won't meet the goal, show confirmation modal
      if (!willMeetGoal) {
        setShowConfirmationModal(true);
        return;
      }
      
      // Otherwise proceed with submission
      submitReinvestment(values);
    },
  });
  
  // Function to handle actual reinvestment submission
  const submitReinvestment = async (values: any) => {
    try {
      setIsSubmitting(true);

      // Parse dates properly
      const startDate = parseAnyDateFormat(values.startDate);
      const endDate = parseAnyDateFormat(values.endDate);

      if (!startDate || !endDate) {
        throw new Error('Invalid date format');
      }

      // Calculate duration in months based on start and end date
      const durationInMonths =
        (endDate.getFullYear() - startDate.getFullYear()) * 12 +
        (endDate.getMonth() - startDate.getMonth());

      // Format data for API request according to specified structure
      const requestData = {
        goalName: title,
        targetAmount: Number(values.newSavingsGoal.replace(/,/g, '')),
        monthlyContribution: values.preferredAmount,
        duration: durationInMonths,
        frequency: values.savingsFrequency,
        autoRew: values.autoWithdrawal, // autoRew as specified in the request body
        status: 'Active',
      };

      // Call the API endpoint
      const response = await appAxios.post(
        `/create-reinvest-goal`,
        requestData
      );

      if (response.data && response.data.statusCode === 200) {
        toast.success('Reinvestment successful');
        if (onReinvest) {
          onReinvest(values);
        }
        handleModalClose();
      } else {
        throw new Error(
          response.data?.message || 'Failed to create reinvestment'
        );
      }
    } catch (error) {
      toast.error(error as string); 
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to handle modal close action with form reset
  const handleModalClose = () => {
    // Reset form
    formik.resetForm();
    
    // Reset all other state
    setShowCalendar(false);
    setAmountExceedsGoal(false);
    setSuggestedAmount(null);
    setSelectedDuration(null);
    setCustomDate(null);
    setShowConfirmationModal(false);
    
    // Close the modal
    onClose();
  };

  // Handle duration click
  const handleDurationClick = (duration: string) => {
    setSelectedDuration(duration);
    formik.setFieldValue('savingsDuration', duration);

    // Get the existing start date or use current date if not set
    const startDate = formik.values.startDate
      ? parseAnyDateFormat(formik.values.startDate)
      : new Date();
    
    if (!startDate) return;

    let endDate = new Date(startDate);

    if (duration === 'Customize') {
      setShowCalendar(true);
      setCustomDate(null);
      // Don't override endDate, let the user choose from the date picker
    } else {
      setShowCalendar(false);
      setCustomDate(null);

      let monthsToAdd = 1;
      if (duration === '1 Month') {
        monthsToAdd = 1;
      } else if (duration === '3 Months') {
        monthsToAdd = 3;
      } else if (duration === '6 Months') {
        monthsToAdd = 6;
      } else if (duration === '9 Months') {
        monthsToAdd = 9;
      } else if (duration === '1 Year') {
        monthsToAdd = 12;
      }

      // Correctly add months to start date
      endDate.setMonth(startDate.getMonth() + monthsToAdd);

      // If adding months goes to the next year, make sure the year is updated properly
      if (endDate.getMonth() !== (startDate.getMonth() + monthsToAdd) % 12) {
        endDate.setDate(0); // Go to the last day of the previous month
      }

      // Set end date in DD/MM/YYYY format
      formik.setFieldValue('endDate', formatDate(endDate));
    }
  };

  // Update end date based on goal amount and preferred amount
  useEffect(() => {
    if (
      formik.values.startDate &&
      formik.values.savingsFrequency &&
      formik.values.newSavingsGoal &&
      formik.values.preferredAmount &&
      selectedDuration === 'Customize'
    ) {
      // Parse start date
      const startDate = parseAnyDateFormat(formik.values.startDate);
      if (!startDate) return;

      let endDate = new Date(startDate); // Clone startDate

      const targetAmount = Number(
        formik.values.newSavingsGoal.replace(/,/g, '') || '0'
      );
      const contribution = Number(formik.values.preferredAmount);
      const frequency = formik.values.savingsFrequency;

      if (!isNaN(targetAmount) && !isNaN(contribution) && contribution > 0) {
        const totalPeriods = Math.ceil(targetAmount / contribution); // Calculate total periods

        switch (frequency.toLowerCase()) {
          case 'daily':
            endDate.setDate(startDate.getDate() + totalPeriods);
            break;
          case 'weekly':
            endDate.setDate(startDate.getDate() + totalPeriods * 7);
            break;
          case 'monthly':
            endDate.setMonth(startDate.getMonth() + totalPeriods);
            break;
          default:
            return;
        }

        // Format the date to dd/mm/yyyy
        const formattedEndDate = formatDate(endDate);

        if (formik.values.endDate !== formattedEndDate) {
          formik.setFieldValue('endDate', formattedEndDate);
        }
      }
    }
  }, [
    formik.values.startDate,
    formik.values.savingsFrequency,
    formik.values.newSavingsGoal,
    formik.values.preferredAmount,
    selectedDuration
  ]);

  // Update end date when frequency changes
  useEffect(() => {
    // Only run this if we have the necessary values and either not in custom mode or recalculating
    if (
      formik.values.startDate &&
      formik.values.newSavingsGoal &&
      formik.values.savingsFrequency &&
      formik.values.preferredAmount &&
      (selectedDuration !== 'Customize')
    ) {
      // Parse start date
      const startDate = parseAnyDateFormat(formik.values.startDate);
      if (!startDate) return;

      let endDate = new Date(startDate); // Clone startDate

      // Get contribution amount and target amount
      const targetAmount = Number(
        formik.values.newSavingsGoal.replace(/,/g, '') || '0'
      );
      const contribution = Number(formik.values.preferredAmount || '0');

      if (!isNaN(targetAmount) && !isNaN(contribution) && contribution > 0) {
        const totalPeriods = Math.ceil(targetAmount / contribution); // Calculate total periods
        const frequency = formik.values.savingsFrequency;

        switch (frequency.toLowerCase()) {
          case 'daily':
            endDate.setDate(startDate.getDate() + totalPeriods);
            break;
          case 'weekly':
            endDate.setDate(startDate.getDate() + totalPeriods * 7);
            break;
          case 'monthly':
            endDate.setMonth(startDate.getMonth() + totalPeriods);
            break;
          default:
            return;
        }

        // Format the date to dd/mm/yyyy
        const formattedEndDate = formatDate(endDate);

        if (formik.values.endDate !== formattedEndDate) {
          formik.setFieldValue('endDate', formattedEndDate);
        }
      }
    }
  }, [formik.values.savingsFrequency]);

  // Improved suggested amount calculation
  useEffect(() => {
    if (!formik.values.newSavingsGoal || !formik.values.savingsFrequency) {
      setSuggestedAmount(null);
      return;
    }

    try {
      // Parse target amount, removing commas
      const targetAmount = Number(
        formik.values.newSavingsGoal.replace(/,/g, '') || 0
      );
      
      const calculatedAmount = calculateSuggestedAmount(
        targetAmount,
        formik.values.savingsFrequency,
        formik.values.savingsDuration,
        formik.values.startDate,
        formik.values.endDate
      );
      
      setSuggestedAmount(calculatedAmount);
    } catch (error) {
      console.error('Error calculating suggested amount:', error);
      setSuggestedAmount(null);
    }
  }, [
    formik.values.newSavingsGoal,
    formik.values.savingsFrequency,
    formik.values.startDate,
    formik.values.endDate,
    formik.values.savingsDuration
  ]);

  // Check if amount exceeds goal amount
  useEffect(() => {
    if (formik.values.newSavingsGoal && formik.values.preferredAmount) {
      const parsedTarget = Number(
        formik.values.newSavingsGoal.replace(/,/g, '')
      );
      setAmountExceedsGoal(Number(formik.values.preferredAmount) > parsedTarget);
    }
  }, [formik.values.newSavingsGoal, formik.values.preferredAmount]);

  const formatWithCommas = (value: string) => {
    const sanitizedValue = value.replace(/[^0-9]/g, '');
    return sanitizedValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const handleAmountChange = (value: number) => {
    formik.setFieldValue('preferredAmount', value);

    if (formik.values.newSavingsGoal) {
      const parsedTarget = Number(
        formik.values.newSavingsGoal.replace(/,/g, '')
      );
      setAmountExceedsGoal(value > parsedTarget);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'newSavingsGoal') {
      const plainValue = value.replace(/,/g, '');
      const formattedValue = formatWithCommas(plainValue);
      formik.setFieldValue(name, formattedValue);
      
      // Recalculate suggested amount when target amount changes
      if (formik.values.savingsFrequency) {
        const targetAmount = Number(plainValue);
        if (!isNaN(targetAmount) && targetAmount > 0) {
          const calculated = calculateSuggestedAmount(
            targetAmount,
            formik.values.savingsFrequency,
            formik.values.savingsDuration,
            formik.values.startDate,
            formik.values.endDate
          );
          setSuggestedAmount(calculated);
        }
      }
    } else {
      formik.handleChange(e);
    }
  };

  // Add this confirmation modal component before the return statement
  const ConfirmationModal = () => {
    if (!showConfirmationModal) return null;
    
    // Calculate values for the modal
    const totalContributions = calculateTotalContributions(formik.values);
    const targetAmount = parseFloat(formik.values.newSavingsGoal.replace(/,/g, '') || '0');
    const willMeetGoal = Math.abs(totalContributions - targetAmount) < 0.01;
    const isShortfall = totalContributions < targetAmount;
    const isExcess = totalContributions > targetAmount;
    const difference = Math.abs(totalContributions - targetAmount);
    
    return (
      <div className="flex overflow-y-auto fixed inset-0 z-[60] justify-center items-center px-5 w-full h-full bg-gray-500 bg-opacity-50">
        <div className="relative p-6 w-full max-w-md bg-white rounded-lg shadow-lg">
          <button
            type="button"
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-900"
            onClick={() => setShowConfirmationModal(false)}
          >
            <X size={20} />
          </button>
          
          <h2 className="mb-4 text-xl font-semibold text-gray-800">
            Savings Plan Summary
          </h2>
          
          {/* Show notice if contributions don't match the target amount */}
          {isShortfall && (
            <div className="p-4 mb-4 text-center bg-red-50 rounded-md border">
              <h3 className="mb-2 font-semibold text-red-600">
                Your savings goal will not be met
              </h3>
              <p className="mb-2 text-red-600">
                With your current plan, you will save {formatToNaira(totalContributions)}
              </p>
              <p className="font-medium text-red-600">
                You will fall short by {formatToNaira(difference)}.
                Would you like to adjust your plan or continue?
              </p>
            </div>
          )}

          {/* Add notice for when contributions exceed the target amount */}
          {isExcess && (
            <div className="p-4 mb-4 bg-yellow-50 rounded-md border border-yellow-200">
              <h3 className="mb-2 font-semibold text-yellow-700">
                Your savings will exceed your goal of {formatToNaira(targetAmount)}
              </h3>
              <p className="mb-2 text-yellow-700">
                Based on your selections, you will save approximately {formatToNaira(totalContributions)}
              </p>
              <p className="font-medium text-yellow-700">
                You will exceed your goal by {formatToNaira(difference)}. 
                Would you like to change this or continue?
              </p>
            </div>
          )}
          
          <div className="flex gap-4 justify-end mt-6">
            <Button
              type="button"
              onClick={() => {
                setShowConfirmationModal(false);
                // Update the target amount to match total contributions
                const newTargetAmount = totalContributions
                  .toString()
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                formik.setFieldValue('newSavingsGoal', newTargetAmount);
                
                // Proceed with submission
                submitReinvestment(formik.values);
              }}
              className="px-6 py-2 text-[#EFA005] bg-white border border-[#EFA005] rounded-md"
            >
              Continue
            </Button>
            <Button
              type="button"
              onClick={() => setShowConfirmationModal(false)}
              className="px-6 py-2 text-white bg-[#EFA005] rounded-md"
            >
              Change Plan
            </Button>
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="flex overflow-y-auto fixed inset-0 z-50 justify-center items-center px-5 w-full h-full bg-gray-500 bg-opacity-50">
      <div
        className="relative p-6 w-full max-w-2xl bg-white rounded-lg shadow-lg max-h-[90vh] overflow-y-auto"
        style={{ scrollbarWidth: 'none' }}
      >
        <style>
          {`
            .modal-content::-webkit-scrollbar {
              display: none;
            }
          `}
        </style>
        <button
          type="button"
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-900"
          onClick={handleModalClose}
        >
          <X size={20} />
        </button>

        <h2 className="mb-4 text-xl font-semibold text-gray-800">
          Re-invest Savings
        </h2>

        {/* Current Savings Summary Card */}
        <div className="p-4 mb-4 text-center bg-gray-50 rounded-md">
          <p className="text-3xl font-bold text-gray-900">{amount}</p>
          <span className="inline-block px-3 py-1 mt-2 text-sm text-green-600 bg-green-100 rounded-lg">
            Ready to Reinvest
          </span>
        </div>

        {/* Current Savings Details */}
        <div className="mb-6 space-y-3">
          <div className="flex justify-between items-center text-gray-700">
            <span>Savings Title</span>
            <span className="font-medium">{title}</span>
          </div>
          {details.map((item, index) => (
            <div
              key={`${item.label}-${index}`}
              className="flex justify-between items-center text-gray-700"
            >
              <span>{item.label}</span>
              <span
                className={`font-medium ${
                  item.isSuccess
                    ? 'text-green-600'
                    : item.isPercentage
                      ? 'text-red-600'
                      : ''
                }`}
              >
                {item.value}
              </span>
            </div>
          ))}
        </div>

        <div className="h-[1px] bg-gray-200 my-6"></div>

        <h3 className="mb-4 text-lg font-semibold text-gray-800">
          Create New Savings
        </h3>

        {/* New Savings Form */}
        <form className="space-y-6" onSubmit={formik.handleSubmit}>
          <InputField
            label="Set New Savings Goal"
            type="text"
            name="newSavingsGoal"
            placeholder="e.g 500000"
            value={formatWithCommas(formik.values.newSavingsGoal)}
            onChange={handleInputChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.newSavingsGoal
                ? formik.errors.newSavingsGoal
                : undefined
            }
            required
          />

          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Set Savings Duration
            </label>
            <div className="flex flex-wrap gap-3">
              {durationOptions.map((option) => (
                <Button
                  key={option}
                  variant="outlined"
                  type="button" // Explicitly set type to prevent form submission
                  onClick={() => handleDurationClick(option)}
                  className={`px-4 py-2 border rounded-md text-[#EFA005] ${
                    formik.values.savingsDuration === option
                      ? 'bg-[#EFA0051A] border-[#EFA005] border-2'
                      : 'transparent border-[#EFA005]'
                  }`}
                >
                  {option}
                </Button>
              ))}
            </div>
            {formik.touched.savingsDuration &&
              formik.errors.savingsDuration && (
                <p className="mt-1 text-sm text-red-500">
                  {formik.errors.savingsDuration}
                </p>
              )}
          </div>

          {/* Date picker for start date */}
          <div className="mt-4 w-full rounded-md">
            <label className="block text-sm font-medium text-gray-700">
              Choose Start Date
            </label>
            <div className="mt-1">
              <DatePicker
                selected={parseAnyDateFormat(formik.values.startDate)}
                onChange={(date: Date | null) => {
                  if (date) {
                    // Store date in DD/MM/YYYY format
                    const formattedDate = formatDate(date);
                    formik.setFieldValue('startDate', formattedDate);

                    // Calculate end date based on selected duration
                    if (
                      selectedDuration &&
                      selectedDuration !== 'Customize'
                    ) {
                      let endDate = new Date(date);
                      let monthsToAdd = 1;

                      // Calculate months to add based on selection
                      if (selectedDuration === '3 Months') monthsToAdd = 3;
                      else if (selectedDuration === '6 Months')
                        monthsToAdd = 6;
                      else if (selectedDuration === '9 Months')
                        monthsToAdd = 9;
                      else if (selectedDuration === '1 Year')
                        monthsToAdd = 12;

                      endDate.setMonth(date.getMonth() + monthsToAdd);
                      formik.setFieldValue('endDate', formatDate(endDate));
                    }
                  } else {
                    formik.setFieldValue('startDate', '');
                  }
                }}
                dateFormat="dd/MM/yyyy"
                minDate={new Date()}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#EFA005] focus:border-transparent"
                placeholderText="Select start date"
                wrapperClassName="w-full"
              />
              {formik.touched.startDate && formik.errors.startDate && (
                <p className="mt-1 text-sm text-red-500">
                  {formik.errors.startDate}
                </p>
              )}
            </div>
          </div>

          {/* Show calendar if "Customize" is selected */}
          {showCalendar && (
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700">
                Customise your end date
              </label>
              <div className="mt-1">
                <DatePicker
                  selected={parseAnyDateFormat(formik.values.endDate)}
                  onChange={(date: Date | null) => {
                    if (date) {
                      const formattedDate = formatDate(date);
                      formik.setFieldValue('endDate', formattedDate);
                    } else {
                      formik.setFieldValue('endDate', '');
                    }
                  }}
                  dateFormat="dd/MM/yyyy"
                  minDate={parseAnyDateFormat(formik.values.startDate) || new Date()}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#EFA005] focus:border-transparent"
                  placeholderText="Select end date"
                  wrapperClassName="w-full"
                />
                {formik.touched.endDate && formik.errors.endDate && (
                  <p className="mt-1 text-sm text-red-500">
                    {formik.errors.endDate}
                  </p>
                )}
              </div>
            </div>
          )}

          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">
              How will you like to save?
            </label>
            <div className="flex flex-wrap gap-3">
              {frequencyOptions.map((option) => (
                <Button
                  key={option}
                  variant="outlined"
                  type="button" // Explicitly set type to prevent form submission
                  onClick={() =>
                    formik.setFieldValue(
                      'savingsFrequency',
                      option.toLowerCase()
                    )
                  }
                  className={`px-4 py-2 border rounded-md text-[#EFA005] ${
                    formik.values.savingsFrequency === option.toLowerCase()
                      ? 'bg-[#EFA0051A] border-[#EFA005] border-2'
                      : 'transparent border-[#EFA005]'
                  }`}
                >
                  {option}
                </Button>
              ))}
            </div>
            {formik.touched.savingsFrequency &&
              formik.errors.savingsFrequency && (
                <p className="mt-1 text-sm text-red-500">
                  {formik.errors.savingsFrequency}
                </p>
              )}
          </div>

          {suggestedAmount && (
            <div className="flex gap-3 items-center p-2 rounded-md bg-[#F0EDFF]">
              <p className="text-xs">
                Based on your selection above, you can be saving (₦
                {suggestedAmount?.toLocaleString()}) per{' '}
                {formik.values.savingsFrequency.toLowerCase() === 'daily'
                  ? 'day'
                  : formik.values.savingsFrequency.toLowerCase() === 'weekly'
                    ? 'week'
                    : 'month'}
                .
              </p>
            </div>
          )}

          <Input
            label="Preferred amount to save"
            options={suggestedAmount ? [
              Math.round(suggestedAmount * 0.75),
              suggestedAmount,
              Math.round(suggestedAmount * 1.25),
              Math.round(suggestedAmount * 1.5),
              Math.round(suggestedAmount * 1.75)
            ] : [500, 1000, 1500, 5000, 10000]}
            onChange={handleAmountChange}
            required
          />
          
          {formik.touched.preferredAmount && formik.errors.preferredAmount && (
            <p className="mt-1 text-sm text-red-500">
              {formik.errors.preferredAmount}
            </p>
          )}

          {amountExceedsGoal && (
            <div className="flex gap-3 items-center p-2 rounded-md bg-[#FFF0F0] border border-red-400">
              <IoAlertCircle className="w-5 h-5 text-red-500" />
              <p className="text-xs text-red-600">
                Your preferred amount to save cannot be more than your savings
                goal.
              </p>
            </div>
          )}

          <div className="flex gap-3 items-center">
            <div
              className={`flex items-center cursor-pointer w-[40px] h-[20px] rounded-full transition-all 
              ${formik.values.autoWithdrawal ? 'bg-[#EFA005]' : 'bg-gray-300'}`}
              onClick={() =>
                formik.setFieldValue(
                  'autoWithdrawal',
                  !formik.values.autoWithdrawal
                )
              }
            >
              <div
                className={`w-[25px] h-[25px] bg-white rounded-full shadow-md transform transition-all 
                ${formik.values.autoWithdrawal ? 'translate-x-[15px]' : 'translate-x-0'}`}
              />
            </div>
            <span className="text-sm text-gray-700">
              Do you want to enable auto withdrawal at the end of your savings
              duration?
            </span>
          </div>

          <div className="flex gap-4 justify-end mt-6">
            <Button
              type="button"
              onClick={handleModalClose}
              className="px-6 py-2 text-[#EFA005] bg-white border border-[#EFA005] rounded-md"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                !formik.isValid ||
                !formik.dirty ||
                amountExceedsGoal ||
                isSubmitting
              }
              className={`px-6 py-2 text-white rounded-md ${
                !formik.isValid ||
                !formik.dirty ||
                amountExceedsGoal ||
                isSubmitting
                  ? 'bg-gray-400'
                  : 'bg-[#EFA005]'
              }`}
            >
              {isSubmitting ? 'Processing...' : 'Proceed'}
            </Button>
          </div>
        </form>
        
        {/* Render confirmation modal */}
        {showConfirmationModal && <ConfirmationModal />}
      </div>
    </div>
  );
};

export default ReinvestModal;
